// CANONICAL page packer for the official report appendices.
//
// This file is the single source of truth. It is copied verbatim into each
// panel by `Scripts/sync_report_renderers.py`; do not edit the panel copies.
//
// Sections are packed onto fixed A4 pages up to a line budget, and a long
// section splits across pages with a "part n of m" marker.
//
// Costs are measured in text lines rather than rows, because row heights vary
// enormously: a lesson whose three learning outcomes are full sentences can
// occupy thirty lines on its own, while an assignment row occupies two. A flat
// per-row cost under-counts the first case and the row then overflows the page,
// colliding with the footer and clipping records out of an official document.

// Usable text lines on one A4 page after padding, running header, and footer.
export const PAGE_BUDGET = 40;

// An orange section bar plus its descriptive line.
const HEADING_COST = 5;
// The table's own header row, which may wrap.
const TABLE_HEADER_COST = 2.5;
// Cell padding, expressed as a fraction of a line.
const ROW_PADDING = 0.8;
// Never strand fewer than this many rows at the foot of a page.
const MIN_ORPHAN_ROWS = 2;

// Printable width inside the page margins, in millimetres.
const CONTENT_WIDTH_MM = 174;
// Effective width of one character at the table's 9.5pt body size, calibrated
// against rendered output. This is wider than the raw glyph advance because it
// absorbs inter-word spacing and the ragged right edge left by word wrapping.
const CHAR_WIDTH_MM = 2.4;

/** Characters that fit on one line of a column, given how many columns share the width. */
function charsPerLine(columnCount) {
  return Math.max(8, (CONTENT_WIDTH_MM / Math.max(1, columnCount)) / CHAR_WIDTH_MM);
}

/**
 * Plain text of a cell, for measurement only.
 *
 * A column may render JSX (the Growth evidence column renders a title with a
 * sub-label). Those are short and fixed, so they fall back to a nominal cost
 * rather than being walked.
 */
function cellLength(column, row) {
  try {
    const rendered = column.render(row);
    if (typeof rendered === "string") return rendered.length;
    if (typeof rendered === "number") return String(rendered).length;
    return 24;
  } catch {
    return 24;
  }
}

/** Estimated height of one body row, in lines. */
export function rowCost(row, columns) {
  if (!columns || !columns.length) return 1 + ROW_PADDING;
  const width = charsPerLine(columns.length);
  const lines = columns.reduce(
    (tallest, column) => Math.max(tallest, Math.ceil(cellLength(column, row) / width)),
    1
  );
  return lines + ROW_PADDING;
}

/**
 * Pack sections onto pages.
 *
 * @param sections {Array<{heading, rows, columns, empty}>}
 * @param budget   {number} lines available per page
 * @param reserve  {number} lines already spoken for on the first page
 * @returns {Array<Array<block>>} pages, each an ordered list of blocks
 */
export function paginate(sections, budget = PAGE_BUDGET, reserve = 0) {
  const pages = [];
  let page = [];
  let used = reserve;

  const flush = () => {
    if (page.length) pages.push(page);
    page = [];
    used = 0;
  };

  for (const section of sections) {
    const rows = Array.isArray(section.rows) ? section.rows : [];

    // An empty category still states its absence — that is reportable
    // information, not filler.
    if (!rows.length) {
      const cost = HEADING_COST + 2;
      if (page.length && used + cost > budget) flush();
      page.push({ ...section, rows: [], part: 0, parts: 1 });
      used += cost;
      continue;
    }

    const costs = rows.map(row => rowCost(row, section.columns));
    const overhead = HEADING_COST + TABLE_HEADER_COST;
    const chunks = [];
    let offset = 0;

    // Split the section into page-sized chunks first, so `parts` is accurate
    // before any of them are placed.
    while (offset < rows.length) {
      const first = chunks.length === 0;
      let capacity = (first ? budget - used : budget) - overhead;

      // Not enough room left on the current page to start this section.
      if (first && capacity < costs[offset] && page.length) {
        flush();
        capacity = budget - overhead;
      }

      let taken = 0;
      let spent = 0;
      while (offset + taken < rows.length && (taken === 0 || spent + costs[offset + taken] <= capacity)) {
        spent += costs[offset + taken];
        taken += 1;
        // A single row taller than a whole page is placed alone; a fixed A4
        // sheet cannot do better without discarding recorded text.
        if (taken === 1 && spent > capacity) break;
      }

      chunks.push({ start: offset, end: offset + taken, spent });
      offset += taken;
    }

    chunks.forEach((chunk, index) => {
      const cost = overhead + chunk.spent;
      const remaining = rows.length - chunk.end;

      if (page.length && used + cost > budget) flush();
      // Avoid leaving one lonely row on the next page.
      if (index === 0 && remaining > 0 && remaining < MIN_ORPHAN_ROWS && chunk.end - chunk.start > MIN_ORPHAN_ROWS) {
        chunk.end = rows.length;
      }

      page.push({
        ...section,
        rows: rows.slice(chunk.start, chunk.end),
        part: index,
        parts: chunks.length,
      });
      used += cost;

      if (index < chunks.length - 1) flush();
    });
  }

  flush();
  return pages;
}

/** Suffix shown when one category runs across several pages. */
export function partLabel(block) {
  return block.parts > 1 ? ` · part ${block.part + 1} of ${block.parts}` : "";
}
